/**
 * useStrains Hook
 *
 * Fetches strains data from Supabase with full cultivation configuration.
 * Provides CRUD operations for strain management.
 *
 * US-PE-001: Strains Management Page
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch all strains with extended cultivation data
 * @param {Object} options - Filter options
 * @param {boolean} options.activeOnly - Only fetch active strains (default: false)
 * @returns {Object} { strains, loading, error, refetch }
 */
export function useAllStrains(options = {}) {
  const [strains, setStrains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { activeOnly = false } = options

  const fetchStrains = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('strains')
        .select('*')
        .order('name', { ascending: true })

      if (activeOnly) {
        query = query.eq('active', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      setStrains(data || [])
    } catch (err) {
      console.error('[useAllStrains] Error:', err)
      setError(err.message)
      setStrains([])
    } finally {
      setLoading(false)
    }
  }, [activeOnly])

  useEffect(() => {
    fetchStrains()
  }, [fetchStrains])

  return {
    strains,
    loading,
    error,
    refetch: fetchStrains
  }
}

/**
 * Hook to fetch a single strain by ID with full details
 * @param {string} id - The strain UUID
 * @returns {Object} { strain, loading, error, refetch }
 */
export function useStrain(id) {
  const [strain, setStrain] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchStrain = useCallback(async () => {
    if (!id) {
      setStrain(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('strains')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) {
        throw fetchError
      }

      setStrain(data)
    } catch (err) {
      console.error('[useStrain] Error:', err)
      setError(err.message)
      setStrain(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchStrain()
  }, [fetchStrain])

  return {
    strain,
    loading,
    error,
    refetch: fetchStrain
  }
}

/**
 * Hook providing mutation functions for strain management
 * @returns {Object} { updateStrain, createStrain, deleteStrain, updating, updateError }
 */
export function useStrainMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  const updateStrain = async (id, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      // Don't use .single() as RLS may prevent reading back the updated row
      const { data, error: updateErr } = await supabase
        .from('strains')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()

      if (updateErr) {
        throw updateErr
      }

      // Return first item if available, otherwise return the updates with id
      const updatedStrain = data?.[0] || { id, ...updates }
      return { data: updatedStrain, error: null }
    } catch (err) {
      console.error('[useStrainMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const createStrain = async (strainData) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: createErr } = await supabase
        .from('strains')
        .insert(strainData)
        .select()
        .single()

      if (createErr) {
        throw createErr
      }

      return { data, error: null }
    } catch (err) {
      console.error('[useStrainMutations] Create error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const toggleActive = async (id, active) => {
    return updateStrain(id, { active })
  }

  return {
    updateStrain,
    createStrain,
    toggleActive,
    updating,
    updateError
  }
}

/**
 * Hook to fetch all strain families with their member strains
 * @param {Object} options - Filter options
 * @param {boolean} options.activeOnly - Only fetch active families (default: true)
 * @returns {Object} { families, loading, error, refetch }
 */
export function useStrainFamilies(options = {}) {
  const [families, setFamilies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { activeOnly = true } = options

  const fetchFamilies = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('strain_families')
        .select(`
          id,
          name,
          code,
          default_strain_id,
          display_order,
          active,
          created_at,
          strains!strains_family_id_fkey (
            id,
            name,
            code,
            active
          )
        `)
        .order('display_order', { ascending: true })

      if (activeOnly) {
        query = query.eq('active', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) throw fetchError

      setFamilies(data || [])
    } catch (err) {
      console.error('[useStrainFamilies] Error:', err)
      setError(err.message)
      setFamilies([])
    } finally {
      setLoading(false)
    }
  }, [activeOnly])

  useEffect(() => {
    fetchFamilies()
  }, [fetchFamilies])

  return {
    families,
    loading,
    error,
    refetch: fetchFamilies
  }
}

/**
 * Hook providing mutation functions for strain family management
 * @returns {Object} { updateFamily, createFamily, updating, updateError }
 */
export function useStrainFamilyMutations() {
  const [updating, setUpdating] = useState(false)
  const [updateError, setUpdateError] = useState(null)

  const updateFamily = async (id, updates) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: updateErr } = await supabase
        .from('strain_families')
        .update(updates)
        .eq('id', id)
        .select()

      if (updateErr) throw updateErr

      return { data: data?.[0] || { id, ...updates }, error: null }
    } catch (err) {
      console.error('[useStrainFamilyMutations] Update error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  const createFamily = async (familyData) => {
    setUpdating(true)
    setUpdateError(null)

    try {
      const { data, error: createErr } = await supabase
        .from('strain_families')
        .insert(familyData)
        .select()
        .single()

      if (createErr) throw createErr

      return { data, error: null }
    } catch (err) {
      console.error('[useStrainFamilyMutations] Create error:', err)
      setUpdateError(err.message)
      return { data: null, error: err }
    } finally {
      setUpdating(false)
    }
  }

  return {
    updateFamily,
    createFamily,
    updating,
    updateError
  }
}

export default useAllStrains
